import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, MapPin, Phone, MessageCircle, Award, TrendingUp, CheckCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PropertyCard from '@/components/PropertyCard';
import LeadForm from '@/components/LeadForm';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';

const BuilderDetail = () => {
  const { builder } = useParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBuilderProperties = async () => {
      try {
        const response = await api.get(`/properties?builder=${encodeURIComponent(builder)}`);
        setProperties(response.data);
      } catch (error) {
        console.error('Error fetching builder properties:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBuilderProperties();
  }, [builder]);

  // Builder information (can be moved to backend later)
  const builderInfo = {
    description: `${builder} is one of India's most trusted real estate developers, known for creating premium properties with world-class amenities. As an official channel partner, we offer exclusive access to their latest projects with the best prices and payment plans.`,
    specialties: [
      'Premium residential projects',
      'World-class amenities',
      'Prime locations',
      'Trusted brand reputation',
    ],
    achievements: [
      { label: 'Projects Delivered', value: '50+' },
      { label: 'Happy Families', value: '10,000+' },
      { label: 'Years of Excellence', value: '25+' },
    ],
  };

  return (
    <div className="min-h-screen bg-dark" data-testid="builder-detail-page">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[500px] flex items-center justify-center overflow-hidden mt-32 bg-gradient-to-br from-dark via-dark-light to-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-rose-gold/20 to-transparent"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center justify-center w-28 h-28 bg-gradient-to-br from-rose-gold to-rose-dark rounded-full mb-8 shadow-rose-glow">
              <Building2 className="text-white" size={48} />
            </div>
            <h1 className="font-heading text-6xl md:text-8xl font-bold text-white mb-6" data-testid="builder-name">
              {builder}
            </h1>
            <p className="text-rose-gold text-xl mb-4 font-semibold uppercase tracking-wider">
              Official Channel Partner
            </p>
            <p className="text-white/70 text-lg max-w-3xl mx-auto leading-relaxed mb-8">
              {properties.length} Premium Projects Available
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#projects" className="btn-primary inline-flex items-center gap-2 text-sm uppercase tracking-wider">
                View Projects
              </a>
              <a href="#book-visit" className="btn-outline inline-flex items-center gap-2 text-sm uppercase tracking-wider">
                Book Site Visit
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Builder Introduction */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-dark-light/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">About {builder}</p>
              <h2 className="font-heading text-5xl font-bold text-white mb-6">
                Premium Real Estate Developer
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                {builderInfo.description}
              </p>
              
              <div className="space-y-4 mb-8">
                <h3 className="font-heading text-2xl font-semibold text-white mb-4">Key Specialties</h3>
                {builderInfo.specialties.map((specialty, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="text-rose-gold flex-shrink-0" size={20} />
                    <span className="text-white/80">{specialty}</span>
                  </div>
                ))}
              </div>

              <a href="tel:+917020970161" className="btn-primary inline-flex items-center gap-2 text-sm uppercase tracking-wider">
                <Phone size={18} />
                Call for {builder} Projects
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-3 gap-6"
            >
              {builderInfo.achievements.map((achievement, index) => (
                <div key={index} className="glass-effect p-6 rounded-2xl border border-dark-lighter text-center">
                  <p className="font-heading text-4xl font-bold text-rose-gold mb-2">{achievement.value}</p>
                  <p className="text-white/60 text-sm uppercase tracking-wider">{achievement.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose as Channel Partner */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-dark">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Why Book Through <span className="text-rose-gold">PropVanta?</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-12">
              As an official channel partner for {builder}, we offer exclusive benefits that you won't get by booking directly
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-effect p-8 rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all"
            >
              <TrendingUp className="text-rose-gold mb-4 mx-auto" size={40} />
              <h3 className="font-heading text-xl font-semibold text-white mb-3">Best Prices</h3>
              <p className="text-white/60 text-sm">Exclusive channel partner pricing and payment plans</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-effect p-8 rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all"
            >
              <Award className="text-rose-gold mb-4 mx-auto" size={40} />
              <h3 className="font-heading text-xl font-semibold text-white mb-3">Expert Guidance</h3>
              <p className="text-white/60 text-sm">Personalized consultation and property selection support</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-effect p-8 rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all"
            >
              <CheckCircle className="text-rose-gold mb-4 mx-auto" size={40} />
              <h3 className="font-heading text-xl font-semibold text-white mb-3">End-to-End Support</h3>
              <p className="text-white/60 text-sm">From site visit to possession, we handle everything</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-24 px-6 md:px-12 lg:px-24 bg-dark-light/50">
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">Premium Collection</p>
            <h2 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4">
              {builder} Projects
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Explore exclusive properties from {builder} with personalized guidance
            </p>
          </motion.div>
        </div>

        {loading ? (
          <div className="text-center text-white/60">Loading projects...</div>
        ) : properties.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10" data-testid="builder-properties-grid">
            {properties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-white/60 text-lg mb-6">No projects currently available from {builder}.</p>
            <a href="/projects" className="btn-outline inline-block text-sm uppercase tracking-wider">
              View All Projects
            </a>
          </div>
        )}
      </section>

      {/* Book Site Visit CTA */}
      <section id="book-visit" className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-dark to-dark-light">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">Book Now</p>
            <h2 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Visit {builder} Projects
              <br />
              <span className="text-rose-gold">With Us</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              We'll personally take you to {builder} properties, explain all the details, and help you make the right investment decision.
            </p>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <CheckCircle className="text-rose-gold flex-shrink-0 mt-1" size={20} />
                <p className="text-white/80">Free site visits to all {builder} projects</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-rose-gold flex-shrink-0 mt-1" size={20} />
                <p className="text-white/80">Expert comparison and selection guidance</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-rose-gold flex-shrink-0 mt-1" size={20} />
                <p className="text-white/80">Exclusive channel partner pricing</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:+917020970161" className="btn-primary inline-flex items-center gap-2 text-sm uppercase tracking-wider">
                <Phone size={18} />
                Call Now
              </a>
              <a href="https://wa.me/917020970161" target="_blank" rel="noopener noreferrer" className="btn-outline inline-flex items-center gap-2 text-sm uppercase tracking-wider border-green-500 text-green-500 hover:bg-green-500 hover:text-white">
                <MessageCircle size={18} />
                WhatsApp
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <LeadForm 
              leadType="builder_site_visit" 
              title={`Book ${builder} Site Visit`}
            />
          </motion.div>
        </div>
      </section>

      {/* Contact Banner */}
      <section className="py-16 bg-gradient-to-r from-rose-gold/20 to-rose-dark/20 border-y border-rose-gold/30">
        <div className="px-6 md:px-12 lg:px-24 text-center">
          <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Interested in {builder} Properties?
          </h3>
          <p className="text-white/80 text-lg mb-8">
            Call us now for personalized recommendations and exclusive pricing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+917020970161" className="btn-primary inline-flex items-center gap-2 text-sm uppercase tracking-wider">
              <Phone size={18} />
              +91 70209 70161
            </a>
            <a href="https://wa.me/917020970161" target="_blank" rel="noopener noreferrer" className="glass-effect border-2 border-green-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-green-500 transition-all inline-flex items-center gap-2 text-sm uppercase tracking-wider">
              <MessageCircle size={18} />
              Message on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BuilderDetail;